import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBillingQuota } from '../api';

const DIMENSION_CONFIG = {
  accounts: { label: '账号数量', limitKey: 'max_accounts' },
  tasks: { label: '任务数量', limitKey: 'max_tasks' },
  monthly_publishes: { label: '月发布次数', limitKey: 'monthly_publish_limit' },
  storage_bytes: { label: '存储空间', limitKey: 'max_storage_bytes' },
};

const toNonNegativeInt = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

const formatStorage = (bytes) => {
  const n = toNonNegativeInt(bytes);
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2).replace(/\.00$/, '')}GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2).replace(/\.00$/, '')}MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(2).replace(/\.00$/, '')}KB`;
  return `${n}B`;
};

export const useBillingQuota = () => {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshQuota = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getBillingQuota();
      if (resp?.status === 'success') {
        setQuota(resp);
        return resp;
      }
      return null;
    } catch (_) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshQuota().catch(() => {});
  }, [refreshQuota]);

  const isExhausted = useCallback((dimension) => {
    const key = String(dimension || '').trim();
    if (!key || !DIMENSION_CONFIG[key]) return false;
    const remaining = quota?.remaining?.[key];
    if (remaining === null || remaining === undefined) return false;
    return toNonNegativeInt(remaining) <= 0;
  }, [quota]);

  const getHint = useCallback((dimension) => {
    const key = String(dimension || '').trim();
    const config = DIMENSION_CONFIG[key];
    if (!config) return '当前额度不足，请升级订阅套餐。';

    const limits = quota?.plan?.limits || {};
    const usage = quota?.usage || {};
    const limit = toNonNegativeInt(limits?.[config.limitKey]);
    const used = toNonNegativeInt(usage?.[key]);
    const usageText = key === 'storage_bytes'
      ? `${formatStorage(used)} / ${formatStorage(limit)}`
      : `${used} / ${limit}`;

    if (limit > 0) {
      return `当前${config.label}额度已用完（${usageText}），升级套餐可继续使用。`;
    }
    return `当前${config.label}额度不足，升级套餐可继续使用。`;
  }, [quota]);

  return useMemo(() => ({
    quota,
    loading,
    refreshQuota,
    isExhausted,
    getHint,
  }), [quota, loading, refreshQuota, isExhausted, getHint]);
};

export default useBillingQuota;
